/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  NOT_PROCEEDING_OTHER_MAX_LENGTH,
  notProceedingFieldVisibility,
  reEngageDateNotInPast,
  requiredNotProceedingReason,
  requiredNotProceedingReasonOther,
  selectableStageOptions,
} from '@gh/builder-shared';
import { Validators } from '@angular/forms';
import { SalesFolderRestService, SalesFolderStatus } from '@gh/builder-shared-data';
import { ContextRoute, getContextRouteInitData } from '@gh/common-internal-shared';
import {
  AbstractSnapshotFormComponent,
  DateTimeTypes,
  DictionaryItem,
  EnumDictionary,
  FormGroupSchemaConfig,
  FormState,
  FormStateBuilder,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  toAppFormGroup,
  updateSchemaWithChildren,
} from '@gh/common-shared';
import { assign, isString } from 'lodash';
import {
  LeadSalesFolder,
  LeadSalesFolderMeeting,
  LeadSfJobDetails,
} from '../../../resources/lead.model';
import { LeadDetailsCommandService } from '../lead-details-command.service';
import { ID } from '@gh/common-shared-data';
import { FormMode, isEditFormMode } from '@gh/core-ui';
import { LeadDetailsBusinessService } from '../../../resources/lead-details-business.service';
import { Subscription } from 'rxjs';

const EDITABLE_SALES_FOLDER_STATUSES: SalesFolderStatus[] = [
  SalesFolderStatus.Active,
  SalesFolderStatus.WillNotComplete,
];

@Component({
  standalone: false,
  selector: 'gh-lead-details-sf-form',
  templateUrl: 'lead-details-sf-form.component.html',
  styleUrls: ['lead-details-sf-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeadDetailsSfFormComponent extends AbstractSnapshotFormComponent<LeadSalesFolder> implements OnInit {
  @Input() jobDetails?: LeadSfJobDetails;
  @Input() sfId?: ID;
  @Input() formMode: FormMode;
  @Output() activityChange = new EventEmitter();

  filteredStatuses: EnumDictionary<SalesFolderStatus>;
  notProceedingReasons: any[];
  otherMaxLength = NOT_PROCEEDING_OTHER_MAX_LENGTH;
  priorities: DictionaryItem<any>[];
  stages: DictionaryItem<any>[];
  /** stages without Closed Won / Closed Lost unless the folder is already there (see selectableStageOptions). */
  selectableStages: DictionaryItem<any>[] = [];
  followUp?: Date;
  earliestActivityId?: ID;
  private onActivityChanges: Subscription;
  private statuses: EnumDictionary<SalesFolderStatus>;

  get isFollowUpDateVisible(): boolean {
    return this.formMode !== FormMode.CreateMode && (!!this.sfId && !this.sfId.includes('sf:')) ;
  }

  get editMode(): boolean {
    return isEditFormMode(this.formMode);
  }

  get isEditActivityActive(): boolean {
    return  !!this.earliestActivityId && !!this.followUp && this.formMode === FormMode.EditMode;
  }

  constructor(private route: ContextRoute,
              private leadService: LeadDetailsBusinessService,
              private commands: LeadDetailsCommandService,
              private sfService: SalesFolderRestService,
              private changeDetection: ChangeDetectorRef,
              private formStateBuilder: FormStateBuilder) {
    super();
  }

  get isDppJobExist(): boolean {
    const { jobDetails } = this;
    return jobDetails ? jobDetails.isDpp : false;
  }

  get isRegularJobExist(): boolean {
    const { jobDetails } = this;
    return jobDetails ? !jobDetails.isDpp : false;
  }

  ngOnInit(): void {
    const init = getContextRouteInitData(this.route);
    this.onActivityChanges = this.leadService.activityWasChanges.subscribe((status) => {
      if (this.sfId && !this.sfId.startsWith('sf:') && status !== null) {
        this.sfService.get(this.sfId).subscribe((sf) => {
          this.followUp = sf.followUp;
          this.earliestActivityId = sf.earliestActivityId;
          this.changeDetection.markForCheck();
        });
      }
    });
    super.ngOnInit();

    assign(this, init);
    const salesFolder = getFormStateSnapshotValue(this.state);
    this.buildStatusDictionary(salesFolder.status);
    this.selectableStages = selectableStageOptions(this.stages, salesFolder.stage);
    this.enableEmitting(false);
    this.setNotProceedingReasons(salesFolder.notProceedingReason);
    this.updateNotProceedingReasonOtherVisibility(salesFolder.status, salesFolder.notProceedingReason);
    this.updateReEngageDateVisibility(salesFolder.status, salesFolder.notProceedingReason);
    this.enableEmitting(true);
  }

  onStatusChange(): void {
    const isStatusWillNotComplete = this.form.value.status === SalesFolderStatus.WillNotComplete;
    this.form.get('notProceedingReason').visible = isStatusWillNotComplete;
    if (isStatusWillNotComplete) {
      this.form.get('notProceedingReason').enable();
      this.form.get('notProceedingReason').markAsTouched();
      this.updateNotProceedingReasonOtherVisibility(this.form.value.status, this.form.value.notProceedingReason);
      this.updateReEngageDateVisibility(this.form.value.status, this.form.value.notProceedingReason);
      return;
    }
    this.form.get('notProceedingReason').setValue('');
    this.form.get('notProceedingReason').disable();
    this.form.get('notProceedingReason').reset();
    this.clearNotProceedingReasonOther();
    this.clearReEngageDate();
  }

  onNotProceedingReasonChange(): void {
    this.updateNotProceedingReasonOtherVisibility(this.form.value.status, this.form.value.notProceedingReason);
    this.updateReEngageDateVisibility(this.form.value.status, this.form.value.notProceedingReason);
  }

  onEditActivity(): void {
    if (!!this.sfId && isString(this.sfId)) {
      const meeting = {
        allDay: false,
        id: this.earliestActivityId,
        activityType: '',
        startDate: new Date(),
        endDate: new Date(),
        guests: [],
        location: '',
        description: '',
        notes: '',
        isDone: false,
        assignee: '',
        active: true,
      } as LeadSalesFolderMeeting;
      this.commands.meetingOpen(this.sfId, meeting, () => {
        this.leadService.activityWasChanges.next(meeting.id);
        this.activityChange.emit();
      });
    }
  }

  protected buildForm(snapshot: FormStateSnapshot<LeadSalesFolder>): FormState<LeadSalesFolder> {
    const salesFolder = getFormStateSnapshotValue(snapshot);
    this.followUp = salesFolder.followUp;
    this.earliestActivityId = salesFolder.earliestActivityId;
    this.changeDetection.markForCheck();
    return this.formStateBuilder.buildPlain({
      snapshot,
      ...this.getSalesFolderSchema(salesFolder),
    });
  }

  protected afterRestored(current: FormStateSnapshot<LeadSalesFolder>): void {
    this.followUp = current.value.followUp;
    this.earliestActivityId = current.value.earliestActivityId;
    this.changeDetection.markForCheck();
    const salesFolder = getFormStateSnapshotValue(current);
    updateSchemaWithChildren(
      toAppFormGroup(this.form.group),
      this.getSalesFolderSchema(salesFolder));
    this.buildStatusDictionary(salesFolder.status);
    this.selectableStages = selectableStageOptions(this.stages, salesFolder.stage);
    this.updateNotProceedingReasonOtherVisibility(salesFolder.status, salesFolder.notProceedingReason);
    this.updateReEngageDateVisibility(salesFolder.status, salesFolder.notProceedingReason);
  }

  private setNotProceedingReasons(value?: string | number): void {
    if (value) {
      this.form.getControl('notProceedingReason').setValue(`${value}`);
    }
  }

  private updateNotProceedingReasonOtherVisibility(
    status?: SalesFolderStatus,
    notProceedingReason?: string | number,
  ): void {
    const { otherVisible } = notProceedingFieldVisibility(status, notProceedingReason);
    const otherControl = this.form.get('notProceedingReasonOther');
    if (otherVisible) {
      otherControl.visible = true;
      if (this.enabled) {
        otherControl.enable();
      } else {
        otherControl.disable();
      }
      return;
    }
    this.clearNotProceedingReasonOther();
  }

  private clearNotProceedingReasonOther(): void {
    const otherControl = this.form.get('notProceedingReasonOther');
    otherControl.setValue('');
    otherControl.disable();
    otherControl.reset();
    otherControl.visible = false;
  }

  private updateReEngageDateVisibility(
    status?: SalesFolderStatus,
    notProceedingReason?: string | number,
  ): void {
    const { reEngageVisible } = notProceedingFieldVisibility(status, notProceedingReason);
    const dateControl = this.form.get('reEngageDate');
    if (!dateControl) {
      return;
    }
    if (reEngageVisible) {
      dateControl.visible = true;
      if (this.enabled) {
        dateControl.enable();
      } else {
        dateControl.disable();
      }
      return;
    }
    this.clearReEngageDate();
  }

  private clearReEngageDate(): void {
    const dateControl = this.form.get('reEngageDate');
    if (!dateControl) {
      return;
    }
    dateControl.setValue(undefined);
    dateControl.disable();
    dateControl.reset();
    dateControl.visible = false;
  }

  private buildStatusDictionary(status: SalesFolderStatus): void {
    this.filteredStatuses = status === SalesFolderStatus.Completed
      ? this.statuses
      : this.statuses.filter((item) => EDITABLE_SALES_FOLDER_STATUSES.includes(item.id));
  }

  private getSalesFolderSchema(sf: LeadSalesFolder): FormGroupSchemaConfig {
    const visibility = notProceedingFieldVisibility(sf.status, sf.notProceedingReason);
    return {
      enabled: this.enabled,
      children: {
        name: { required: true },
        priority: { required: true },
        created: { enabled: false, type: DateTimeTypes.ShortDate },
        followUp: { type: DateTimeTypes.ShortDate, enabled: false },
        status: { required: true, enabled: sf.status !== SalesFolderStatus.Completed },
        notProceedingReason: {
          required: true,
          enabled: visibility.reasonVisible,
          visible: visibility.reasonVisible,
          validators: [requiredNotProceedingReason],
        },
        notProceedingReasonOther: {
          required: true,
          enabled: visibility.otherVisible,
          visible: visibility.otherVisible,
          validators: [requiredNotProceedingReasonOther, Validators.maxLength(NOT_PROCEEDING_OTHER_MAX_LENGTH)],
        },
        reEngageDate: {
          type: DateTimeTypes.ShortDate,
          enabled: visibility.reEngageVisible,
          visible: visibility.reEngageVisible,
          validators: [reEngageDateNotInPast],
        },
        // A completed sale's stage is set by Complete Sale (backend: ManualStageChangePolicy).
        stage: { required: true, enabled: sf.status !== SalesFolderStatus.Completed },
      },
    };
  }
}
